import React, { useEffect, useState } from "react";
import { Card, Empty, Flex, Input, Typography } from "antd";
import { HistoryOutlined } from "@ant-design/icons";
import { HistorySection } from "@/components/history/HistorySection";
import type { HistoryModule } from "@/services/history.api";

const { Text } = Typography;

/**
 * Los módulos del historial que son del Catálogo. Todos son **globales** —se leen con acceso
 * al Catálogo, no a un store— y por eso ninguno admite notas (ver abajo).
 */
export type CatalogHistoryModule = Extract<
  HistoryModule,
  "product" | "attribute" | "attribute_set" | "category" | "catalog_admin" | "import"
>;

/**
 * El encabezado de las tarjetas del Catálogo, el mismo del historial (abajo) y de Pedidos:
 * 40px, gris, con borde. Lo comparten las tarjetas de Store › Categorías (xcons-docs#449)
 * para verse iguales al historial que tienen debajo.
 */
export const TARJETA_CON_ENCABEZADO = {
  style: { border: "1px solid #d9d9d9" },
  header: {
    height: 40,
    minHeight: 40,
    padding: "0 16px",
    background: "#f0f0f0",
    borderBottom: "1px solid #d9d9d9",
  },
  titulo: { fontSize: 14, fontWeight: 600 },
} as const;

interface Props {
  module: CatalogHistoryModule;
  /**
   * La clave de la línea de tiempo, la misma que usa el API: el `entity_id` (o el SKU de un
   * alta) en un producto, el `attribute_code` en un atributo, el id en un conjunto, una
   * categoría o una corrida del importador, y `'catalog'` en los Admins del Catálogo, que son
   * una sola línea de tiempo. Sin entidad (el importador antes de abrir una corrida) la
   * tarjeta se ve igual, vacía.
   */
  entityId?: string | number;
  /**
   * Cambiarlo vuelve a pedir la línea de tiempo. Lo usan las pantallas que editan sin
   * desmontar el panel (el árbol de categorías): sin esto el evento recién grabado no aparece
   * hasta salir y volver a entrar.
   */
  recarga?: number;
  /** Para meterlo dentro de otra tarjeta sin sumar un margen que la pantalla no tenía. */
  style?: React.CSSProperties;
}

/**
 * El historial de cualquier cosa del Catálogo, **igual al de Presupuestos y Pedidos**
 * (xcons-docs#404): la misma tarjeta `history-card`, el encabezado gris de 40px y el buscador
 * en el encabezado, con la misma espera antes de buscar mientras se escribe.
 *
 * Nació como el historial del producto (PIM Fase 12) y se generalizó en xcons-docs#436 para
 * que atributos, conjuntos, categorías, Admins y corridas del importador se vean con el mismo
 * look, en vez de cinco copias que se desalinean.
 *
 * **Sin notas.** No es una decisión de UI: una nota se graba con un store y sus mencionables
 * se resuelven por store/seller, y el Catálogo no tiene ninguno de los dos. Ver el prop
 * `notes` del componente.
 */
export const HistorialDelCatalogo: React.FC<Props> = ({ module, entityId, recarga = 0, style }) => {
  const [busqueda, setBusqueda] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setQ(busqueda.trim()), 350);
    return () => clearTimeout(t);
  }, [busqueda]);

  return (
    <Card
      className="history-card order-history-card"
      style={style}
      styles={{
        header: { height: 40, minHeight: 40, padding: "0 16px" },
        body: {
          padding: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
      title={
        <Flex align="center" gap={24} style={{ width: "100%" }}>
          <Flex gap={4} align="center" style={{ flexShrink: 0 }}>
            <HistoryOutlined style={{ fontSize: 16 }} />
            <Text style={{ fontSize: 14, fontWeight: 600 }}>Historial</Text>
          </Flex>
          <Flex flex={1} gap={16} align="center">
            <Input.Search
              size="small"
              allowClear
              placeholder="Buscar en el historial..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              style={{ flex: 1 }}
            />
          </Flex>
        </Flex>
      }
    >
      <div style={{ padding: 12 }}>
        {/* `key`: cambiar de entidad sin desmontar (el drawer de atributos se reusa, el árbol
            cambia de categoría) tiene que arrancar la línea de tiempo de cero, no mezclar la
            anterior; y `recarga` la vuelve a pedir después de un guardado. */}
        {entityId == null ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sin registros" />
        ) : (
          <HistorySection
            key={`${module}:${entityId}:${recarga}`}
            module={module}
            entityId={entityId}
            notes={false}
            externalQ={q}
          />
        )}
      </div>
    </Card>
  );
};
